import React, { FC } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

interface EnergyInput {
  energyCarrier: string
  energyConsumption: number
  consumptionUnit: string
  consumptionSource: string
  note?: string
}

interface MaterialInput {
  materialName: string
  netWeight: number
  weightUnit: string
  source: string
  note?: string
}

interface LogisticsInput {
  logisticsName: string
  distance: number
  netWeight?: number
  weightUnit: string
  source: string
  note?: string
}

interface SubAssemblyInput {
  assemblyName: string
  netWeight: number
  weightUnit: string
  note?: string
}

interface Step {
  name: string
  energy?: EnergyInput[]
  material?: MaterialInput[]
  logistics?: LogisticsInput[]
  subAssemblies?: SubAssemblyInput[]
}

interface Props {
  data: Step[]
}

const CollapsibleLcaTable: FC<Props> = ({ data }) => {
  return (
    <div className="space-y-4">
      {data.map((step, idx) => {
        const energy = step.energy || []
        const materials = step.material || []
        const logistics = step.logistics || []
        const subAssemblies = step.subAssemblies || []

        return (
          <Accordion key={idx} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography className="font-semibold text-lg">
                {step.name}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* ENERGY */}
              {energy.length > 0 && (
                <div className="mb-4">
                  <Typography variant="subtitle1" className="font-semibold">
                    Energy Inputs
                  </Typography>
                  <Divider className="mb-2" />
                  {energy.map((e, i) => (
                    <div key={i} className="mb-2 text-sm">
                      🔌 <strong>{e.energyCarrier}</strong>:{' '}
                      {e.energyConsumption} {e.consumptionUnit}
                      <br />
                      <em className="text-xs text-gray-500">
                        {e.consumptionSource}
                      </em>
                      {e.note && (
                        <div className="text-xs italic text-gray-400">
                          📝 {e.note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* MATERIALS */}
              {materials.length > 0 && (
                <div className="mb-4">
                  <Typography variant="subtitle1" className="font-semibold">
                    Material Inputs
                  </Typography>
                  <Divider className="mb-2" />
                  {materials.map((m, i) => (
                    <div key={i} className="mb-2 text-sm">
                      🧱 <strong>{m.materialName}</strong>: {m.netWeight}{' '}
                      {m.weightUnit}
                      <br />
                      <em className="text-xs text-gray-500">{m.source}</em>
                      {m.note && (
                        <div className="text-xs italic text-gray-400">
                          📝 {m.note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* LOGISTICS */}
              {logistics.length > 0 && (
                <div className="mb-4">
                  <Typography variant="subtitle1" className="font-semibold">
                    Logistics Inputs
                  </Typography>
                  <Divider className="mb-2" />
                  {logistics.map((l, i) => (
                    <div key={i} className="mb-2 text-sm">
                      🚚 <strong>{l.logisticsName}</strong>: {l.distance} km
                      {l.netWeight !== undefined && (
                        <>
                          {' '}
                          — {l.netWeight} {l.weightUnit}
                        </>
                      )}
                      <br />
                      <em className="text-xs text-gray-500">{l.source}</em>
                      {l.note && (
                        <div className="text-xs italic text-gray-400">
                          📝 {l.note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* SUB-ASSEMBLIES */}
              {subAssemblies.length > 0 && (
                <div className="mb-2">
                  <Typography variant="subtitle1" className="font-semibold">
                    Sub-Assemblies
                  </Typography>
                  <Divider className="mb-2" />
                  {subAssemblies.map((s, i) => (
                    <div key={i} className="mb-2 text-sm">
                      🧩 <strong>{s.assemblyName}</strong>: {s.netWeight}{' '}
                      {s.weightUnit}
                      {s.note && (
                        <div className="text-xs italic text-gray-400">
                          📝 {s.note}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        )
      })}
    </div>
  )
}

export default CollapsibleLcaTable
